import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WifiOff, Loader2, Zap, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export const TheMirror: React.FC = () => {
  const [mode, setMode] = useState<'old' | 'new'>('old');
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <section className="py-24 bg-gray-50 dark:bg-black overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {t('theMirror.title')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto text-lg">
            {t('theMirror.subtitle')}
          </p>
        </div>

        {/* Switcher */}
        <div className="flex justify-center mb-12">
          <div className="bg-white dark:bg-gray-900 p-1.5 rounded-full border border-gray-200 dark:border-gray-800 flex relative shadow-lg">
            <button
              onClick={() => setMode('old')}
              className={`relative z-10 px-6 py-2 text-sm font-medium rounded-full transition-colors duration-300 ${
                mode === 'old' ? 'text-gray-900' : 'text-gray-500 hover:text-gray-900 dark:text-gray-500 dark:hover:text-gray-300'
              }`}
            >
              {t('theMirror.switch.old')}
            </button>
            <button
              onClick={() => setMode('new')}
              className={`relative z-10 px-6 py-2 text-sm font-medium rounded-full transition-colors duration-300 ${
                mode === 'new' ? 'text-white' : 'text-gray-500 hover:text-gray-900 dark:text-gray-500 dark:hover:text-gray-300'
              }`}
            >
              {t('theMirror.switch.new')}
            </button>
            
            {/* Sliding Background */}
            <motion.div
              initial={false}
              animate={{
                x: mode === 'old' ? 0 : '100%',
                backgroundColor: mode === 'old' ? '#e5e7eb' : '#06b6d4'
              }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="absolute left-1.5 top-1.5 bottom-1.5 w-[calc(50%-6px)] rounded-full"
            />
          </div>
        </div>

        {/* Content Container */}
        <div className="relative mx-auto max-w-4xl aspect-video bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-2xl overflow-hidden">
          <AnimatePresence mode="wait">
            {mode === 'old' ? (
              <motion.div
                key="old"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 flex flex-col"
              >
                {/* Old Header - Clunky */}
                <div className="h-16 bg-gray-200 border-b border-gray-300 flex items-center px-8 justify-between">
                  <div className="w-32 h-8 bg-gray-300 rounded animate-pulse"></div>
                  <div className="flex gap-4">
                    <div className="w-20 h-4 bg-gray-300 rounded hidden md:block"></div>
                    <div className="w-20 h-4 bg-gray-300 rounded hidden md:block"></div>
                  </div>
                </div>
                
                {/* Old Body - Slow & Broken */}
                <div className="flex-1 bg-gray-100 p-8 relative">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-4">
                    <Loader2 className="w-12 h-12 text-gray-400 animate-spin" />
                    <span className="text-gray-400 font-serif">{t('theMirror.old.loading')}</span>
                  </div>
                  
                  {/* Simulated Content fading in poorly */}
                  <div className="space-y-6 opacity-20 blur-[1px]">
                    <div className="h-8 bg-gray-300 w-3/4 rounded"></div>
                    <div className="h-4 bg-gray-300 w-full rounded"></div>
                    <div className="h-4 bg-gray-300 w-5/6 rounded"></div>
                    <div className="w-40 h-10 bg-blue-900 rounded shadow-sm flex items-center justify-center text-white font-serif">
                      {t('theMirror.old.contact')}
                    </div>
                  </div>

                  {/* Error/Warning */}
                  <div className="absolute bottom-4 right-4 bg-yellow-100 text-yellow-800 px-4 py-2 rounded border border-yellow-300 text-xs flex items-center gap-2">
                    <WifiOff size={14} /> {t('theMirror.old.error')}
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="new"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-slate-950 flex flex-col text-white"
              >
                {/* New Header - Sleek */}
                <div className="h-20 border-b border-white/10 bg-slate-900/50 backdrop-blur-md flex items-center px-8 justify-between relative z-50">
                  <div className="font-bold text-xl tracking-tight">
                    {t('theMirror.new.brand')}<span className="text-cyan-400">{t('theMirror.new.brandSuffix')}</span>
                  </div>
                  <div className="relative">
                    <button 
                      onClick={() => setMenuOpen(!menuOpen)}
                      className="bg-white/10 hover:bg-white/20 text-white px-6 py-2 rounded-full text-sm font-medium transition-all border border-white/5"
                    >
                      {t('theMirror.new.menu')}
                    </button>
                    
                    <AnimatePresence>
                      {menuOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.95 }}
                          className="absolute right-0 top-full mt-2 w-48 bg-slate-900 border border-slate-700 rounded-xl shadow-xl overflow-hidden"
                        >
                          <div className="py-1">
                            {[
                              { label: 'Home', path: '/' },
                              { label: 'About', path: '/about' },
                              { label: 'Servizi', path: '/solutions' },
                              { label: 'Contacts', path: '/contact' },
                              { label: 'Login', path: '/login' }
                            ].map((item) => (
                              <button
                                key={item.path}
                                onClick={() => {
                                  navigate(item.path);
                                  setMenuOpen(false);
                                }}
                                className="block w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-cyan-400 transition-colors"
                              >
                                {item.label}
                              </button>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                {/* New Body - High Perf */}
                <div className="flex-1 relative p-6 md:p-12 flex flex-col lg:block justify-center">
                  <div className="relative z-10 max-w-lg w-full lg:w-auto flex flex-col justify-center h-full">
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.2 }}
                      className="inline-flex items-center gap-2 text-cyan-400 text-xs font-mono mb-4 border border-cyan-500/20 bg-cyan-950/30 px-3 py-1 rounded-full w-fit"
                    >
                      <Zap size={12} /> {t('theMirror.new.performance')}
                    </motion.div>
                    <motion.h3 
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.3 }}
                      className="text-3xl md:text-5xl font-bold leading-tight mb-6"
                    >
                      {t('theMirror.new.headline')} <br/>
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600">{t('theMirror.new.headlineHighlight')}</span>
                    </motion.h3>
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.4 }}
                      className="flex gap-4"
                    >
                       <button 
                         onClick={() => navigate('/contact')}
                         className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold px-8 py-3 rounded-xl flex items-center gap-2 transition-transform hover:scale-105"
                       >
                         {t('theMirror.new.cta')} <ArrowRight size={16} />
                       </button>
                    </motion.div>
                  </div>

                  {/* Visual Elements */}
                  <div className="absolute right-0 top-0 bottom-0 w-1/2 bg-gradient-to-l from-cyan-500/10 to-transparent pointer-events-none hidden lg:block"></div>
                  <motion.div 
                    className="relative mt-8 lg:mt-0 lg:absolute lg:right-12 lg:top-1/2 lg:-translate-y-1/2 w-full max-w-[280px] mx-auto lg:mx-0 lg:w-64 bg-slate-900 border border-slate-700 p-6 rounded-2xl shadow-2xl"
                    initial={{ x: 50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.5, type: "spring" }}
                  >
                     <div className="flex items-center justify-between mb-4 border-b border-slate-800 pb-4">
                        <div className="text-sm text-slate-400">{t('theMirror.new.conversion')}</div>
                        <div className="text-green-400 font-mono">+214%</div>
                     </div>
                     <div className="flex gap-1 h-20 items-end">
                        {[40, 65, 55, 80, 95].map((h, i) => (
                          <motion.div 
                            key={i}
                            initial={{ height: 0 }}
                            animate={{ height: `${h}%` }}
                            transition={{ delay: 0.6 + (i * 0.1), duration: 0.5 }}
                            className="flex-1 bg-gradient-to-t from-cyan-600 to-blue-500 rounded-t-sm opacity-80 hover:opacity-100 transition-opacity"
                          ></motion.div>
                        ))}
                     </div>
                  </motion.div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
